import React from 'react';

import PartialForm from './partial-form';
import { queryCEP, states as getStates, cities as getCities } from '../services/geo';

import Form, { Partial } from '@angellira/form';
import { withSnackbar } from 'notistack';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';

import CEPInput, { conform } from '../components/cep-input';

import pipe from '../utils/pipe';
import * as O from '../utils/object';
import * as validations from '../utils/validations';

const addressInputs = ['stateId', 'cityId', 'neighborhoodName', 'address', 'number'];

export const availableFields = [
  'cep', 'stateId', 'cityId',
  'neighborhoodName', 'address', 'number'
];

export function extract(data) {
  const filter = O.filter(O.onlyProps(availableFields));
  const mapping = O.remap({
    cep: (value, prop, obj) => conform((obj.place ? obj.place.cep : obj.cep) || ''),
    state: (v, p, obj) => obj.city ? obj.city.state : '',
    neighborhoodName: (v, p, obj) => obj.neighborhood
      ? obj.neighborhood.name
      : obj.neighborhoodName,
    address: (v, p, obj) => obj.place ? obj.place.address : ''
  });

  return pipe(Object.assign({}, data), mapping, O.join(data), O.flatIDs, filter);
}

export class AddressForm extends PartialForm {

  static defaultProps = {
    onChange: Function.prototype,
    // When form was externally filled
    onFill: Function.prototype
  };

  state = {
    loading: false,
    states: [],
    cities: [],
    error: {}
  }

  constructor(props) {
    super(props);

    this.inputRefs = {
      state: React.createRef(),
      city: React.createRef(),
      neighborhood: React.createRef(),
      place: React.createRef(),
      number: React.createRef()
    };

    this.state.loading = this.props.loading || false;
  }

  fetchStates() {
    getStates().then(({ data: states }) => this.setState({ states }));
  }

  fetchCities() {
    getCities(this.props.value.stateId)
      .then(({ data: cities }) => this.setState({ cities }));
  }

  componentDidMount() {
    this.fetchStates();

    this.hasInput('stateId') && this.fetchCities();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value.stateId !== this.getFormValue('stateId'))
      this.fetchCities();
  }

  enqueueSnackbar(...args) {
    return this.props.enqueueSnackbar && this.props.enqueueSnackbar(...args);
  }

  firstEmptyInput() {
    return addressInputs.find(input => !this.hasInput(input)) || 'stateId';
  }

  handleCEPQuery = ev => this.hasInput('cep') && this.setLoading()
    .then(() => queryCEP(this.getFormValue('cep').replace(/\D/g, '')))
    .then(({ status, data }) => status === 200
      ? Promise.resolve(data)
      : Promise.reject(data))
    .then(({ place, neighborhood, city, state }) => {
      const values = {
        ...this.props.value,
        neighborhoodName: neighborhood.name,
        neighborhoodId: neighborhood.id,
        cityId: city.id,
        address: place.address,
        placeId: place.id,
        stateId: state.id
      };

      this.props.onChange(ev, values);
      this.props.onFill(ev, values);
    })
    .then(() => this.fetchCities())
    .catch(e => e.response.status === 404
      ? this.enqueueSnackbar(
        'Este CEP não foi encontrado em nossa base de dados',
        { variant: 'error' }
      )
      : this.enqueueSnackbar(`Ocorreu um erro ao buscar o CEP: ${e}`, { variant: 'error' })
    ).finally(() => this.setLoading(false).then(() => this
      .inputRefs[this.firstEmptyInput()]
      .current
      .focus()
    ));

  render() {
    const {
      loading, onPresentSnackbar, enqueueSnackbar, closeSnackbar, onFill,
      ...rest
    } = this.props;

    return <Form {...rest} treatError={e => ({ error: !!e, helperText: e })}>
      <Grid container spacing={32}>
        <Grid item sm={4} xs={12}>
          <Partial
            el={CEPInput}
            handle={'cep'}
            pattern={validations.cep}
            render={(ref, props) => (
              <TextField
                fullWidth
                required
                label="CEP"
                margin="dense"
                inputRef={ref}
                onBlur={this.handleCEPQuery}
                disabled={this.isLoading()}
                InputLabelProps={{ shrink: this.hasInput('cep') }}
                InputProps={{
                  endAdornment: this.getAdornment() || <InputAdornment position="end">
                    <IconButton onClick={this.handleCEPQuery}>
                      <SearchIcon/>
                    </IconButton>
                  </InputAdornment>
                }}
                {...props}
              />
            )}
          />
        </Grid>

        <Grid item sm={4} xs={12}>
          <Partial
            el={TextField}
            pattern={validations.oneOf(this.state.states.map(x => x.id))}
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            extract={ev => ev.target.value}
            inputRef={this.inputRefs.estado}
            label="Estado"
            margin="dense"
            { ...this.defaultHandle('stateId') }
          >
            <option key={-1} value=""></option>
            {this.state.states.map(({ name, id }) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </Partial>
        </Grid>

        <Grid item sm={4} xs={12}>
          <Partial
            el={TextField}
            pattern={validations.oneOf(this.state.cities.map(x => x.id))}
            fullWidth
            required
            select
            SelectProps={{ native: true }}
            extract={ev => ev.target.value}
            inputRef={this.inputRefs.city}
            label="Cidade"
            margin="dense"
            { ...this.defaultHandle('cityId') }
          >
            <option key={-1} value=""></option>
            {this.state.cities.map(({ name, id }) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </Partial>
        </Grid>

        <Grid item sm={4} xs={12}>
          <Partial
            el={TextField}
            fullWidth
            required
            inputRef={this.inputRefs.neighborhood}
            pattern={validations.requiredString}
            label="Bairro"
            margin="dense"
            { ...this.defaultHandle('neighborhoodName') }
          />
        </Grid>

        <Grid item sm={5} xs={12}>
          <Partial
            el={TextField}
            fullWidth
            required
            inputRef={this.inputRefs.address}
            pattern={validations.requiredString}
            label="Logradouro"
            margin="dense"
            { ...this.defaultHandle('address') }
          />
        </Grid>

        <Grid item sm={3} xs={12}>
          <Partial
            el={TextField}
            fullWidth
            inputRef={this.inputRefs.number}
            pattern={validations.number}
            label="Número"
            margin="dense"
            { ...this.defaultHandle('number') }
          />
        </Grid>
      </Grid>
    </Form>;
  }
}

export default withSnackbar(AddressForm);
